import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { fileUrl } from "@/lib/upload";
import { useConfig } from "@/context/ConfigContext";
import { statusInfo } from "@/lib/i18n";
import { initials } from "@/lib/format";

export const BRAND_DEFAULTS = {
  "brand.logo_url": "/brand/logo_fond-sombre.png",
  "brand.logo_url_light": "/brand/logo_fond-clair.png",
  "brand.symbol_url": "/brand/symbole_fond-sombre.png",
  "brand.symbol_url_light": "/brand/symbole_fond-clair.png",
  "brand.favicon_url": "/favicon-32x32.png",
  "brand.pwa_icon_url": "/android-chrome-512x512.png",
  "brand.og_image_url": "/brand/image-partage_1200x630.png",
  "brand.email_logo_url": "/brand/logo_fond-clair.png",
};

const useBrand = (dark, light) => {
  const { settings, theme } = useConfig();
  return theme === "light" ? settings[light] || BRAND_DEFAULTS[light] : settings[dark] || BRAND_DEFAULTS[dark];
};

export const Symbol = ({ className = "h-8 w-8", testId = "brand-symbol" }) => {
  const url = useBrand("brand.symbol_url", "brand.symbol_url_light");
  return <img src={url} alt="Monrezo" className={`object-contain ${className}`} data-testid={testId} />;
};

export const Logo = ({ to = "/", testId = "logo-link", compact = false }) => {
  const url = useBrand("brand.logo_url", "brand.logo_url_light");
  return (
    <Link to={to} data-testid={testId} className="inline-flex items-center p-1 -m-1">
      {compact ? <Symbol className="h-8 w-8" testId={`${testId}-symbol`} /> : <img src={url} alt="Monrezo" className="h-6 w-auto max-w-none" data-testid={`${testId}-img`} />}
    </Link>
  );
};

export const ThemeToggle = () => {
  const { theme, setTheme } = useConfig();
  const light = theme === "light";
  return (
    <button
      type="button"
      data-testid="theme-toggle-button"
      aria-label={light ? "Passer en mode sombre" : "Passer en mode clair"}
      onClick={() => setTheme(light ? "dark" : "light")}
      className="grid place-items-center w-11 h-11 rounded-[3px] border border-mr-border text-mr-text2 hover:text-mr-text hover:bg-mr-raised transition-colors"
    >
      {light ? <Moon size={17} /> : <Sun size={17} />}
    </button>
  );
};

export const Eyebrow = ({ children, className = "" }) => <div className={`mr-eyebrow ${className}`}>{children}</div>;

const toneStyle = (tone) => {
  const c = tone === "muted" ? "var(--mr-text2)" : `var(--mr-${tone})`;
  return { color: c, borderColor: `color-mix(in srgb, ${c} 40%, transparent)`, background: `color-mix(in srgb, ${c} 10%, transparent)` };
};

export const StatusBadge = ({ status, label, tone, testId }) => {
  const [l, t] = statusInfo(status);
  return (
    <span data-testid={testId} style={toneStyle(tone || t)} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label || l}
    </span>
  );
};

export const EmptyState = ({ icon: Icon, title, text, action, testId = "empty-state" }) => (
  <div data-testid={testId} className="mr-card text-center py-12 px-6 border-dashed">
    {Icon ? <Icon className="mx-auto mb-4 text-mr-muted" size={28} strokeWidth={1.5} /> : <Symbol className="mx-auto mb-4 h-8 w-8 opacity-60" testId={`${testId}-symbol`} />}
    <p className="font-display font-semibold text-lg">{title}</p>
    {text && <p className="text-sm text-mr-text2 mt-2 max-w-sm mx-auto">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Loader = ({ rows = 3 }) => (
  <div className="space-y-3" data-testid="loader">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-16 rounded-[3px] bg-mr-surface border border-mr-border animate-pulse" />
    ))}
  </div>
);

export function Avatar({ name, size = 40, fileId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    setUrl(null);
    if (fileId) fileUrl(fileId).then(setUrl).catch(() => {});
  }, [fileId]);
  return (
    <div style={{ width: size, height: size }} className="shrink-0 grid place-items-center rounded-full overflow-hidden bg-mr-raised border border-mr-border text-xs font-semibold text-mr-text2">
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : initials(name)}
    </div>
  );
}

export const PageTitle = ({ eyebrow, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
      <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{title}</h1>
    </div>
    {children}
  </div>
);

export const PlusMarks = () => (
  <>
    <span className="mr-plus -top-2 -left-1.5">+</span>
    <span className="mr-plus -top-2 -right-1.5">+</span>
    <span className="mr-plus -bottom-2 -left-1.5">+</span>
    <span className="mr-plus -bottom-2 -right-1.5">+</span>
  </>
);

export const SimBanner = ({ children }) => (
  <div data-testid="simulation-banner" style={toneStyle("info")} className="border border-dashed text-sm rounded-[3px] px-4 py-3">
    {children}
  </div>
);
